import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Loader2, MoreVertical, Users, LogIn, CheckCircle, XCircle, AlertTriangle, Trash2, X, KeyRound, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Filter, Mail, Phone, User as UserIcon, BarChart3, CreditCard, RefreshCw } from 'lucide-react';
import { SuperAdminLayout } from '@/components/super-admin/SuperAdminLayout';
import { saApi } from '@/contexts/SuperAdminAuthContext';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { PasswordResetModal } from '@/components/PasswordResetModal';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { useSidebarEdge } from '@/lib/useSidebarEdge';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { SelectField } from '@/components/ui/SelectField';

interface Org {
  id: string; name: string; ownerEmail: string; ownerName: string;
  plan: string; planPrice: number; status: string; totalLeads: number; totalUsers: number; joinedAt: string;
}

interface OrgUser {
  id: string; name: string; email: string; role: string; isActive: boolean; lastLogin: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  active:    'bg-emerald-50 text-emerald-700',
  inactive:  'bg-gray-100 text-gray-600',
  suspended: 'bg-red-50 text-red-700',
};

// ── 3-dot Action Menu ──────────────────────────────────────────────────────────
function ActionMenu({ org, onViewUsers, onImpersonate, onStatus, onDelete, onResetPassword, loading }: {
  org: Org;
  onViewUsers: () => void;
  onImpersonate: () => void;
  onStatus: (s: string) => void;
  onDelete: () => void;
  onResetPassword: () => void;
  loading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Position the fixed menu against the button, flipping ABOVE when there
  // isn't enough room below (bottom rows would otherwise clip off-screen).
  // Measured after render so the actual menu height drives the decision.
  useLayoutEffect(() => {
    if (!open || !menuRef.current || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuH = menuRef.current.offsetHeight;
    const menuW = menuRef.current.offsetWidth;
    const vh = window.innerHeight;
    let top = rect.bottom + 4;
    if (top + menuH > vh - 8) top = Math.max(8, rect.top - menuH - 4);
    const left = Math.max(8, rect.right - menuW);
    setMenuPos({ top, left });
  }, [open]);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      // First-paint estimate; the layout effect corrects it precisely.
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 192) });
    }
    setOpen(o => !o);
  };

  const item = (label: string, icon: React.ReactNode, onClick: () => void, cls = '') => (
    <button
      key={label}
      onClick={() => { onClick(); setOpen(false); }}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 text-left transition-colors ${cls}`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div ref={ref}>
      <button
        ref={btnRef}
        onClick={handleToggle}
        disabled={loading}
        className="p-1.5 rounded-md text-[#94A3B8] hover:text-[#64748B] hover:bg-gray-100 transition-colors disabled:opacity-40"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
      </button>

      {open && (
        <div
          ref={menuRef}
          className="fixed z-50 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {item('View details', <Users className="h-3.5 w-3.5 text-[#6366F1]" />, onViewUsers)}
          {item('Login as Org', <LogIn className="h-3.5 w-3.5 text-[#6366F1]" />, onImpersonate)}
          {item('Reset Owner Password', <KeyRound className="h-3.5 w-3.5 text-amber-600" />, onResetPassword)}
          <div className="border-t border-gray-100 my-1" />
          {org.status !== 'active' && item('Activate', <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />, () => onStatus('active'))}
          {org.status !== 'inactive' && item('Deactivate', <XCircle className="h-3.5 w-3.5 text-gray-500" />, () => onStatus('inactive'))}
          {org.status !== 'suspended' && item('Suspend', <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />, () => onStatus('suspended'))}
          <div className="border-t border-gray-100 my-1" />
          {item('Delete Org', <Trash2 className="h-3.5 w-3.5 text-red-500" />, onDelete, 'text-red-600')}
        </div>
      )}
    </div>
  );
}

// ── Organization detail drawer (Lead Detail style) ────────────────────────────
interface OrgDetail {
  id: string; name: string; slug: string; createdAt: string; upgradeCtaEnabled: boolean;
  owner: { id: string; name: string; email: string; phone: string | null; emailVerified: boolean; lastLogin: string | null } | null;
  subscription: { status: string; planId: string | null; planName: string; planPrice: number; currentPeriodEnd: string; since: string } | null;
  usage: { totalLeads: number; leadsThisMonth: number; leadLimit: number; totalUsers: number; activeUsers: number; userLimit: number };
  lastLeadAt: string | null;
  lastLogin: { at: string; name: string } | null;
  leadTrend: { month: string; count: number }[];
  invoices: { id: string; provider: string; planName: string | null; amountCents: number; currency: string; status: string; periodEnd: string | null; createdAt: string }[];
}

const nf = (n: number) => n.toLocaleString('en-IN');
const initialsOf = (name: string) => String(name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?';
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} h ago`;
  const d = Math.floor(sec / 86400);
  return d < 30 ? `${d} d ago` : formatDate(iso);
}

function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const unlimited = limit < 0;
  const pct = unlimited || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const tone = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-[#6366F1]';
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="text-slate-900 tabular-nums">{nf(used)} / {unlimited ? 'Unlimited' : nf(limit)}</span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: unlimited ? '0%' : `${pct}%` }} />
      </div>
    </div>
  );
}

function OrgDetailDrawer({ org, onClose, onImpersonate, onStatus, onDelete, onResetPassword }: {
  org: Org;
  onClose: () => void;
  onImpersonate: () => void;
  onStatus: (s: string) => void;
  onDelete: () => void;
  onResetPassword: () => void;
}) {
  const left = useSidebarEdge();
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [tab, setTab] = useState<'overview' | 'users' | 'billing'>('overview');
  const [users, setUsers] = useState<OrgUser[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Load (or retry) the detail; on failure show an inline error + Retry
  // instead of an endless spinner.
  useEffect(() => {
    setDetail(null);
    setLoadError(null);
    saApi.get(`/super-admin/organizations/${org.id}`)
      .then(({ data }) => setDetail(data))
      .catch((e) => setLoadError(e?.response?.data?.message || 'Failed to load organization'));
  }, [org.id, org.status, reloadKey]);

  useEffect(() => {
    if (tab !== 'users' || users) return;
    saApi.get(`/super-admin/organizations/${org.id}/users`)
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => { setUsers([]); toast('error', 'Failed to load users'); });
  }, [tab, org.id, users]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const status = detail?.subscription?.status ?? org.status;
  const actionBtn = 'inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50';
  const trendMax = Math.max(1, ...(detail?.leadTrend ?? []).map(t => t.count));

  return (
    <div
      className="fixed inset-y-0 right-0 z-50 flex flex-col bg-[#F8F9FA] shadow-2xl border-l border-slate-200 animate-in slide-in-from-right duration-300"
      style={{ left }}
    >
      <div className="flex h-12 sm:h-14 flex-shrink-0 items-center justify-end border-b border-slate-100 bg-white px-3 sm:px-6">
        <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 space-y-4">
        {/* Header card */}
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-r from-white via-white to-indigo-50/70 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-11 w-11 sm:h-14 sm:w-14 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] text-base sm:text-lg font-bold text-white">
                {initialsOf(org.name)}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 break-words">{org.name}</h2>
                <p className="text-sm text-slate-500">{detail?.slug ? `/${detail.slug}` : org.ownerEmail}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {detail?.subscription?.planName ?? org.plan}
                    {(detail?.subscription?.planPrice ?? org.planPrice) > 0 ? ` · $${detail?.subscription?.planPrice ?? org.planPrice}/mo` : ''}
                  </span>
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_COLORS[status] || STATUS_COLORS.inactive}`}>{status}</span>
                  {detail?.upgradeCtaEnabled && (
                    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-50 text-amber-700">Upgrade Plan: On</span>
                  )}
                  <span className="text-xs text-slate-400">Joined {formatDate(org.joinedAt)}</span>
                </div>
              </div>
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <button onClick={onImpersonate} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] px-3 sm:px-3.5 text-sm font-semibold text-white">
                <LogIn className="h-4 w-4" /> <span className="hidden sm:inline">Login as Org</span>
              </button>
              <button onClick={onResetPassword} className={actionBtn}>
                <KeyRound className="h-4 w-4" /> <span className="hidden sm:inline">Reset password</span>
              </button>
              {status !== 'active' && (
                <button onClick={() => onStatus('active')} className={actionBtn}><CheckCircle className="h-4 w-4 text-emerald-500" /> <span className="hidden sm:inline">Activate</span></button>
              )}
              {status !== 'inactive' && (
                <button onClick={() => onStatus('inactive')} className={actionBtn}><XCircle className="h-4 w-4 text-slate-500" /> <span className="hidden sm:inline">Deactivate</span></button>
              )}
              {status !== 'suspended' && (
                <button onClick={() => onStatus('suspended')} className={actionBtn}><AlertTriangle className="h-4 w-4 text-orange-500" /> <span className="hidden sm:inline">Suspend</span></button>
              )}
              <button onClick={onDelete} title="Delete organization" className="inline-flex h-10 items-center rounded-lg border border-red-200 bg-white px-3 text-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Info strip */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
            {([
              [UserIcon, 'Owner', detail?.owner?.name ?? org.ownerName],
              [Mail, 'Email', detail?.owner?.email ?? org.ownerEmail],
              [Phone, 'Phone', detail?.owner?.phone || '—'],
              [Users, 'Users', detail ? `${nf(detail.usage.activeUsers)} active of ${nf(detail.usage.totalUsers)}` : nf(org.totalUsers)],
            ] as [any, string, React.ReactNode][]).map(([Icon, label, value]) => (
              <div key={label} className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-slate-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">{label}</p>
                  <p className="text-sm font-semibold text-slate-800 truncate">{value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-start gap-y-1.5 sm:flex-row sm:flex-wrap sm:items-center gap-x-6 border-t border-slate-100 px-4 py-3 text-sm">
            <span className="text-slate-500">Joined: <span className="font-medium text-slate-800">{formatDate(org.joinedAt)}</span></span>
            <span className="text-slate-500">Last login: <span className="font-medium text-slate-800">{detail?.lastLogin ? `${timeAgo(detail.lastLogin.at)} (${detail.lastLogin.name})` : '—'}</span></span>
            <span className="text-slate-500">Last lead: <span className="font-medium text-slate-800">{timeAgo(detail?.lastLeadAt)}</span></span>
            {detail?.subscription && (
              <span className="text-slate-500">Plan period ends: <span className="font-medium text-slate-800">{formatDate(detail.subscription.currentPeriodEnd)}</span></span>
            )}
            {detail?.owner && (
              <span className="text-slate-500">Owner email: <span className={`font-medium ${detail.owner.emailVerified ? 'text-emerald-700' : 'text-amber-700'}`}>{detail.owner.emailVerified ? 'Verified' : 'Not verified'}</span></span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-slate-100 px-2 sm:px-3 py-2.5">
            {([
              { key: 'overview', label: 'Overview', icon: BarChart3, badge: 0 },
              { key: 'users', label: 'Users', icon: Users, badge: detail?.usage.totalUsers ?? org.totalUsers },
              { key: 'billing', label: 'Billing', icon: CreditCard, badge: detail?.invoices.length ?? 0 },
            ] as const).map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`group flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-3 sm:px-4 py-2 text-sm font-medium transition-colors ${
                    active ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold shadow-sm' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'}`} />
                  {t.label}
                  {t.badge > 0 && (
                    <span className={`min-w-[18px] rounded-full px-1.5 text-center text-[11px] font-semibold leading-[18px] ${active ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-600'}`}>{t.badge}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="p-3 sm:p-5">
            {loadError ? (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Couldn't load details</p>
                  <p className="text-xs text-slate-500 mt-0.5 break-all">{loadError}</p>
                </div>
                <button
                  onClick={() => setReloadKey(k => k + 1)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className="h-4 w-4" /> Retry
                </button>
              </div>
            ) : !detail ? (
              <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
            ) : tab === 'overview' ? (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-900">Plan usage</h3>
                  <UsageMeter label="Leads" used={detail.usage.totalLeads} limit={detail.usage.leadLimit} />
                  <UsageMeter label="Users" used={detail.usage.totalUsers} limit={detail.usage.userLimit} />
                  <p className="text-xs text-slate-500">{nf(detail.usage.leadsThisMonth)} leads added this month.</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">New leads</h3>
                  <p className="text-xs text-slate-500 mb-3">Last 6 months</p>
                  <div className="flex items-end gap-2 h-32">
                    {detail.leadTrend.map(t => (
                      <div key={t.month} className="flex flex-1 flex-col items-center justify-end gap-1 h-full" title={`${t.month}: ${t.count} leads`}>
                        <span className="text-[10px] text-slate-500 tabular-nums">{t.count ? nf(t.count) : ''}</span>
                        <div className="w-full rounded-t bg-[#6366F1]/80 min-h-[3px]" style={{ height: `${(t.count / trendMax) * 85}%` }} />
                        <span className="text-[10px] text-slate-400">
                          {new Date(Number(t.month.slice(0, 4)), Number(t.month.slice(5)) - 1, 1).toLocaleDateString('en-GB', { month: 'short' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : tab === 'users' ? (
              !users ? (
                <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#6366F1]" /></div>
              ) : users.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-400">No users found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                        <th className="px-3 py-2 font-normal">Name</th>
                        <th className="px-3 py-2 font-normal">Email</th>
                        <th className="px-3 py-2 font-normal">Role</th>
                        <th className="px-3 py-2 font-normal">Status</th>
                        <th className="px-3 py-2 font-normal">Last login</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {users.map(u => (
                        <tr key={u.id}>
                          <td className="px-3 py-2.5 text-slate-900 whitespace-nowrap">{u.name}</td>
                          <td className="px-3 py-2.5 text-slate-600">{u.email}</td>
                          <td className="px-3 py-2.5 text-slate-600 capitalize">{u.role}</td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{u.isActive ? 'Active' : 'Inactive'}</span>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{u.lastLogin ? formatDateTime(u.lastLogin) : 'Never'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 text-sm">
                  <div className="rounded-xl border border-slate-100 p-3">
                    <p className="text-xs text-slate-500">Plan</p>
                    <p className="mt-0.5 font-semibold text-slate-900">{detail.subscription?.planName ?? 'No Plan'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 p-3">
                    <p className="text-xs text-slate-500">Price</p>
                    <p className="mt-0.5 font-semibold text-slate-900">{detail.subscription?.planPrice ? `$${detail.subscription.planPrice}/mo` : 'Free'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-100 p-3">
                    <p className="text-xs text-slate-500">Current period ends</p>
                    <p className="mt-0.5 font-semibold text-slate-900">{detail.subscription ? formatDate(detail.subscription.currentPeriodEnd) : '—'}</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Invoices</h3>
                  {detail.invoices.length === 0 ? (
                    <p className="py-6 text-center text-sm text-slate-400">No invoices yet</p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {detail.invoices.map(inv => (
                        <li key={inv.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                          <div className="min-w-0">
                            <p className="text-slate-800">{inv.planName ?? 'Plan'} · <span className="capitalize">{inv.provider}</span></p>
                            <p className="text-xs text-slate-500">{formatDateTime(inv.createdAt)}{inv.periodEnd ? ` · until ${formatDate(inv.periodEnd)}` : ''}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="font-semibold text-slate-900 tabular-nums">{(inv.amountCents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })} {inv.currency.toUpperCase()}</span>
                            <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : inv.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{inv.status}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Organizations Page ─────────────────────────────────────────────────────────
export function SuperAdminOrganizationsPage() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [plans, setPlans] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);
  const [detailOrg, setDetailOrg] = useState<Org | null>(null);
  const [confirmOrg, setConfirmOrg] = useState<Org | null>(null);
  const [confirmOrgLoading, setConfirmOrgLoading] = useState(false);
  const [resetPwdOrg, setResetPwdOrg] = useState<Org | null>(null);

  const fetchOrgs = () => {
    setLoading(true);
    saApi.get('/super-admin/organizations')
      .then(r => {
        setOrgs(r.data);
        const uniquePlans = [...new Set<string>(r.data.map((o: Org) => o.plan).filter((p: string) => p !== 'No Plan'))];
        setPlans(uniquePlans);
      })
      .catch((err) => {
        toast('error', err?.response?.data?.message || 'Failed to load organizations');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOrgs(); }, []);

  // Deep link: /super-admin/organizations?open=<tenantId> opens that org's
  // detail drawer (used from the Revenue page).
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('open');
  useEffect(() => {
    if (!openId || !orgs.length) return;
    const o = orgs.find(x => x.id === openId);
    if (o) setDetailOrg(o);
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('open'); return n; }, { replace: true });
  }, [openId, orgs]);

  const updateStatus = async (id: string, status: string) => {
    setUpdating(id);
    try {
      await saApi.patch(`/super-admin/organizations/${id}/status`, { status });
      fetchOrgs();
      // Keep the open drawer in sync (re-fetches its detail on status change).
      setDetailOrg(d => (d && d.id === id ? { ...d, status } : d));
      toast('success', `Status updated to ${status}`);
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to update status');
    } finally { setUpdating(null); }
  };

  const handleDelete = (org: Org) => {
    if (org.status === 'active' && org.planPrice > 0) {
      toast('error', `Cannot delete "${org.name}" — it has an active paid subscription. Cancel the subscription first.`);
      return;
    }
    setConfirmOrg(org);
  };

  const handleConfirmDelete = async () => {
    if (!confirmOrg) return;
    setConfirmOrgLoading(true);
    setUpdating(confirmOrg.id);
    try {
      await saApi.delete(`/super-admin/organizations/${confirmOrg.id}`);
      if (detailOrg?.id === confirmOrg.id) setDetailOrg(null);
      setConfirmOrg(null);
      fetchOrgs();
      toast('success', `Organization "${confirmOrg.name}" deleted`);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete organization');
    } finally { setUpdating(null); setConfirmOrgLoading(false); }
  };

  const handleImpersonate = async (org: Org) => {
    try {
      const { data } = await saApi.post(`/super-admin/organizations/${org.id}/impersonate`);

      // Backend now returns a full auth payload mirroring /auth/login so
      // AuthContext boots cleanly when the new tab reads localStorage.
      // Previously only `accessToken` was set, which made the tenant app
      // redirect straight to /login because `user` was null.
      localStorage.setItem('accessToken', data.accessToken || data.vendorToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      if (data.user?.id) localStorage.setItem('userId', data.user.id);
      if (data.user) {
        const userData = { ...data.user, tenantId: data.tenantId };
        localStorage.setItem('user', JSON.stringify(userData));
      }
      if (data.tenantId) localStorage.setItem('tenantId', data.tenantId);

      window.open(data.redirectUrl || '/dashboard', '_blank');
      toast('success', `Logged in as ${org.name}`);
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Failed to impersonate org');
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = orgs.filter(o => {
    const matchSearch = !q || o.name.toLowerCase().includes(q) || o.ownerEmail.toLowerCase().includes(q) || o.ownerName.toLowerCase().includes(q);
    const matchPlan = !filterPlan || o.plan === filterPlan;
    const matchStatus = !filterStatus || o.status === filterStatus;
    return matchSearch && matchPlan && matchStatus;
  });
  const hasActiveFilters = !!(q || filterPlan || filterStatus);
  const activeCount = orgs.filter(o => o.status === 'active').length;
  const payingCount = orgs.filter(o => o.status === 'active' && o.planPrice > 0).length;

  const { sorted, sort, onSort } = useSort(filtered);

  // ── Pagination ──────────────────────────────────────────────────────────────
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, filterPlan, filterStatus]); // any filter change → first page
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = sorted.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, sorted.length);

  const selectCls = 'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]';

  const menuFor = (org: Org) => (
    <ActionMenu
      org={org}
      loading={updating === org.id}
      onViewUsers={() => setDetailOrg(org)}
      onImpersonate={() => handleImpersonate(org)}
      onStatus={s => updateStatus(org.id, s)}
      onDelete={() => handleDelete(org)}
      onResetPassword={() => setResetPwdOrg(org)}
    />
  );

  return (
    <SuperAdminLayout title="Organizations">
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Organizations</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {nf(orgs.length)} organizations · {nf(activeCount)} active · {nf(payingCount)} paying
          </p>
        </div>

        {/* Search + filter toggle */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search organization, owner or email..."
              className="h-10 w-72 max-w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            />
          </div>
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`inline-flex h-10 items-center gap-2 rounded-lg border bg-white px-3 text-sm ${showFilters ? 'border-[#6366F1] text-[#6366F1]' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
          >
            <Filter className="h-4 w-4" /> Filters
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {hasActiveFilters && (
            <button onClick={() => { setSearch(''); setFilterPlan(''); setFilterStatus(''); }} className="inline-flex h-10 items-center gap-1 px-2 text-sm text-slate-500 hover:text-slate-800">
              <X className="h-4 w-4" /> Clear
            </button>
          )}
          <span className="text-xs text-slate-400 ml-auto">Showing {filtered.length} of {orgs.length}</span>
        </div>

        {showFilters && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-normal text-slate-600 mb-1 block">Plan</label>
              <SelectField value={filterPlan} onChange={e => setFilterPlan(e.target.value)} className={selectCls}>
                <option value="">All Plans</option>
                {plans.map(p => <option key={p} value={p}>{p}</option>)}
              </SelectField>
            </div>
            <div>
              <label className="text-xs font-normal text-slate-600 mb-1 block">Status</label>
              <SelectField value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={selectCls}>
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </SelectField>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {loading ? (
              <p className="px-4 py-10 text-center text-sm text-slate-400">Loading…</p>
            ) : sorted.length === 0 ? (
              <p className="px-4 py-10 text-center text-sm text-slate-400">No organizations found</p>
            ) : paged.map((org, idx) => (
              <div key={org.id} className="flex items-start gap-3 px-4 py-3">
                <div className="flex-1 min-w-0" onClick={() => setDetailOrg(org)}>
                  <p className="text-sm">
                    <span className="text-xs text-slate-400 mr-1.5">{rangeStart + idx}.</span>
                    <span className="font-medium text-[#6366F1]">{org.name}</span>
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Joined {formatDate(org.joinedAt)}</p>
                  <p className="mt-2 text-sm text-slate-800 truncate">{org.ownerName}</p>
                  <p className="text-xs text-slate-500 truncate">{org.ownerEmail}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-slate-700">{org.plan}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[org.status] || STATUS_COLORS.inactive}`}>{org.status}</span>
                    <span className="text-slate-500">{nf(org.totalLeads)} leads · {nf(org.totalUsers)} users</span>
                  </div>
                </div>
                {menuFor(org)}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <TableHeadRow>
                  <SortTh label="S.No" sortable={false} className="w-12" />
                  {/* Merged like the leads list: Organization = name + joined,
                      Owner = name + email, Plan = plan + status, Usage = leads + users. */}
                  <SortTh label="Organization" sortKey="name" sort={sort} onSort={onSort} className="py-3.5 whitespace-nowrap" />
                  <SortTh label="Owner" sortKey="ownerName" sort={sort} onSort={onSort} className="py-3.5 whitespace-nowrap" />
                  <SortTh label="Plan & Status" sortKey="plan" sort={sort} onSort={onSort} className="py-3.5 whitespace-nowrap" />
                  <SortTh label="Usage" sortKey="totalLeads" sort={sort} onSort={onSort} className="py-3.5 whitespace-nowrap" />
                  <SortTh label="Actions" sortable={false} align="right" className="py-3.5 whitespace-nowrap" />
                </TableHeadRow>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loading ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No organizations found</td></tr>
                ) : paged.map((org, idx) => (
                  <tr key={org.id} className="align-top hover:bg-slate-50/70 transition-colors">
                    <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">{rangeStart + idx}</td>
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setDetailOrg(org)} className="text-left font-medium text-[#6366F1] hover:underline">
                        {org.name}
                      </button>
                      <p className="text-xs text-slate-500 mt-0.5">Joined {formatDate(org.joinedAt)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800">{org.ownerName}</p>
                      <p className="text-xs text-slate-500 mt-0.5 break-all">{org.ownerEmail}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800">{org.plan}{org.planPrice > 0 ? <span className="text-xs text-slate-500"> · ${org.planPrice}/mo</span> : null}</p>
                      <span className={`mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_COLORS[org.status] || STATUS_COLORS.inactive}`}>{org.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800 tabular-nums">{nf(org.totalLeads)} leads</p>
                      <p className="text-xs text-slate-500 mt-0.5 tabular-nums">{nf(org.totalUsers)} users</p>
                    </td>
                    <td className="px-4 py-3 text-right">{menuFor(org)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          {!loading && sorted.length > 0 && (
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 flex-wrap">
              <span className="text-xs text-slate-400">
                Showing <span className="font-medium text-slate-600">{rangeStart}–{rangeEnd}</span> of {sorted.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="h-3.5 w-3.5" /> Prev
                </button>
                <span className="px-2 text-xs text-slate-500">Page {safePage} of {pageCount}</span>
                <button
                  onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                  disabled={safePage >= pageCount}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-600 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Next <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {detailOrg && (
        <OrgDetailDrawer
          org={detailOrg}
          onClose={() => setDetailOrg(null)}
          onImpersonate={() => handleImpersonate(detailOrg)}
          onStatus={s => updateStatus(detailOrg.id, s)}
          onDelete={() => handleDelete(detailOrg)}
          onResetPassword={() => setResetPwdOrg(detailOrg)}
        />
      )}
      <ConfirmModal
        isOpen={!!confirmOrg}
        title="Delete Organization?"
        message={`Permanently delete "${confirmOrg?.name}" and all its data? This cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOrg(null)}
        isLoading={confirmOrgLoading}
      />

      <PasswordResetModal
        isOpen={!!resetPwdOrg}
        title="Reset Owner Password"
        subject={resetPwdOrg ? `${resetPwdOrg.ownerName} · ${resetPwdOrg.ownerEmail}` : ''}
        onConfirm={async (newPassword) => {
          if (!resetPwdOrg) return;
          await saApi.post(
            `/super-admin/organizations/${resetPwdOrg.id}/reset-owner-password`,
            { newPassword },
          );
          toast('success', `Password reset for ${resetPwdOrg.ownerEmail}`);
        }}
        onClose={() => setResetPwdOrg(null)}
      />
    </SuperAdminLayout>
  );
}
